import { useEffect, useState, useRef, useId } from 'react'

interface VideoModalProps {
  isOpen: boolean
  onClose: () => void
  videoUrl?: string
}

export default function VideoModal({ isOpen, onClose, videoUrl }: VideoModalProps) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const videoControlsId = useId()

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
      document.body.style.overflow = 'hidden'
      // Auto-play video when modal opens
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play()
            .then(() => {
              setIsPlaying(true)
            })
            .catch(error => {
              console.error("Video autoplay failed:", error)
            })
        }
      }, 300) // Small delay to ensure modal is visible
    } else {
      document.body.style.overflow = 'unset'
      // Pause video when modal closes
      if (videoRef.current) {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.target as HTMLVideoElement
    setCurrentTime(video.currentTime)
    setProgress((video.currentTime / video.duration) * 100)
    setIsPlaying(!video.paused)
  }

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.target as HTMLVideoElement
    setDuration(video.duration)
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const progressBar = e.currentTarget
    const clickPosition = e.clientX - progressBar.getBoundingClientRect().left
    const newProgress = (clickPosition / progressBar.offsetWidth) * 100
    if (videoRef.current) {
      videoRef.current.currentTime = (newProgress / 100) * duration
    }
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  if (!isOpen && !isAnimating) return null

  return (
		<div
			className={`absolute inset-0 z-[99] flex items-center justify-center ${
				isOpen ? 'animate-fadeIn' : 'animate-fadeOut'
			}`}
			onAnimationEnd={() => {
				if (!isOpen) setIsAnimating(false)
			}}
		>
			{/* Darkened Background */}
			<div className='absolute inset-0 bg-black/50' onClick={onClose}></div>

			{/* Video Container */}
			<div className='relative w-full h-full z-10'>
				{/* Gradient Overlay */}
				<div className='absolute inset-0 bg-black/50 z-[1]'></div>

				{/* Video Controls */}
				<div
					id={`video-controls-${videoControlsId}`}
					className='absolute top-12 right-20 z-10 px-8 flex justify-between items-center bg-gray-400/20 rounded-full py-3 mx-auto w-fit'
				>
					<div className='flex items-center text-white font-brandon gap-6'>
						{'Video'}
						<button
							onClick={togglePlay}
							className='w-8 h-8 flex items-center justify-center text-white'
							aria-label={isPlaying ? 'Pause video' : 'Play video'}
						>
							{isPlaying ? (
								<svg
									xmlns='http://www.w3.org/2000/svg'
									focusable='false'
									viewBox='0 0 24 24'
									color='rgb(244, 242, 239)'
									className='w-8 h-8 fill-current'
								>
									<path d='M6 19h4V5H6v14zm8-14v14h4V5h-4z'></path>
								</svg>
							) : (
								<svg
									xmlns='http://www.w3.org/2000/svg'
									focusable='false'
									viewBox='0 0 24 24'
									color='rgb(244, 242, 239)'
									className='w-8 h-8 fill-current'
								>
									<path d='M8 5v14l11-7z'></path>
								</svg>
							)}
						</button>
					</div>
					<button
						onClick={onClose}
						className='w-8 h-8 flex items-center justify-center text-white ml-6'
						aria-label='Close video'
					>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							focusable='false'
							viewBox='0 0 24 24'
							color='rgb(244, 242, 239)'
							className='w-8 h-8 fill-current'
						>
							<path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'></path>
						</svg>
					</button>
				</div>

				<video
					ref={videoRef}
					className='w-full h-full object-cover relative z-0'
					src={videoUrl || 'https://harmon.wtgdigital.io/assets/2978ae0c-b7fe-4267-8530-c4746ceb483b/general-video.mp4'}
					onTimeUpdate={handleTimeUpdate}
					onLoadedMetadata={handleLoadedMetadata}
					onClick={togglePlay}
					playsInline
					preload='auto'
					loop
				>
					Your browser does not support the video tag.
				</video>

				{/* Custom Controls */}
				{/* <div className='absolute bottom-8 left-6 right-6 md:left-32 md:right-32 z-10'>
					<div
						className='relative w-full h-0.5 bg-white/30 mb-2 cursor-pointer'
						onClick={handleProgressClick}
					>
						<div
							className='absolute left-0 top-0 h-full bg-[#B67D49]'
							style={{ width: `${progress}%` }}
						/>
					</div>
					<div className='flex justify-between text-[#B67D49] font-brandon'>
						<span>{formatTime(currentTime)}</span>
						<span>{formatTime(duration)}</span>
					</div>
				</div> */}

				{/* Play Button Overlay - only shown when video is not playing and not on mobile */}
				{/* {!isPlaying && (
					<button
						className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white/10 flex items-center justify-center md:flex hidden z-10'
						onClick={togglePlay}
						aria-label='Play video'
					>
						<div className='w-16 h-16 rounded-full border-2 border-white flex items-center justify-center'>
							<svg
								width='24'
								height='28'
								viewBox='0 0 12 14'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'
							>
								<path
									d='M0.419719 1.16236C0.419962 1.01618 0.459518 0.872759 0.534244 0.74712C0.608969 0.621481 0.716111 0.518252 0.844443 0.448254C0.972775 0.378254 1.11757 0.344062 1.26366 0.349257C1.40975 0.354452 1.55175 0.398843 1.67479 0.477781L10.7634 6.31793C10.8785 6.39166 10.9732 6.49315 11.0388 6.61307C11.1044 6.73298 11.1388 6.86746 11.1388 7.00414C11.1388 7.14082 11.1044 7.2753 11.0388 7.39521C10.9732 7.51512 10.8785 7.61662 10.7634 7.69035L1.67479 13.5321C1.55175 13.6111 1.40975 13.6555 1.26366 13.6607C1.11757 13.6658 0.972775 13.6317 0.844443 13.5617C0.716111 13.4917 0.608969 13.3884 0.534244 13.2628C0.459518 13.1371 0.419962 12.9937 0.419719 12.8475V1.16236Z'
									fill='white'
								/>
							</svg>
						</div>
					</button>
				)} */}
			</div>
		</div>
	)
} 